import { BCMSRecord, BCMSStateManagerCRUD, useBCMSContext } from "@bcm-dev/bcms-vite";
import { PageContainer, PageContentView, WidgetContainer, WidgetView } from 'components/shared';
import Classes, { Account } from "model";
import React from 'react';
import TASPositivePayViewChecksSearch from "./classes/TASPositivePayViewChecksSearch";
import TASPositivePayViewChecksDesktopFormView from "./components/TASPositivePayViewChecksDesktopFormView";
import TASPositivePayViewChecksMobileFormView from "./components/TASPositivePayViewChecksMobileFormView";
import TASPositivePayViewChecksSearchUI from "./classes/TASPositivePayViewChecksSearchUI";
import TASPositivePayCheckForm from "../TASPositivePayAddChecksPage/classes/TASPositivePayCheckForm";
import TASPositivePayCheckFormUI from "../TASPositivePayAddChecksPage/classes/TASPositivePayCheckFormUI";
import "./styles.css";
import "./mobile.css";

/**
 * @function TASPositivePayViewChecksPage
 * @return {React.Component}
 * @exported true
 * @description The TAS Positive Pay View Checks page.
 */
export default function TASPositivePayViewChecksPage(props) {
  const context = useBCMSContext();
  BCMSRecord.globalControllerFor(Classes, context);
  const data = { ...getDefaultData(undefined, context), ...props.data ?? {} };
  const ui = { ...getDefaultUI(data.search), ...props.ui ?? {} };

  const controller = (state, setState) => ({
    data: state.data,
    ui: state.ui,

    onChange: prop => val => {
        var _val = val;
        if(prop.includes("Amount")) _val = val.amount;

        if(prop.includes("account")) {
          _val = Account.initByType(_val);
        }

        BCMSStateManagerCRUD.init(state)
        .update([ "data", "search", prop ], _val)
        .submit(setState);
    },

    onEdit: (id, prop) => val => {
      let editCheckForms = state.data.search.editCheckForms;
      let findIndex = editCheckForms.findIndex(item => item.id === id);
      let selectedEditCheckForm = editCheckForms.filter(item => item.id === id)[0];
      editCheckForms = editCheckForms.filter(item => item.id !== id);
      const modified = TASPositivePayCheckForm.init({ ...selectedEditCheckForm.record });
      modified[prop] = val;

      if(prop.includes("account")) {
        modified[prop] = Account.initByType(val);
      }

      editCheckForms = [
        ...editCheckForms.slice(0, findIndex),
        modified,
        ...editCheckForms.slice(findIndex)
      ];

      BCMSStateManagerCRUD.init(state)
      .update([ "data", "search", "editCheckForms" ], editCheckForms)
      .submit(setState);
    },

    onEditClick: id => val => {
      let checkForms = state.ui.checkForms;
      let findIndex = state.ui.checkForms.findIndex(item => item.id === id);
      let selectedCheckFormUI = state.ui.checkForms.filter(item => item.id === id)[0];
      checkForms = checkForms.filter(item => item.id !== id);
      let modified = TASPositivePayCheckFormUI.init({ ...selectedCheckFormUI.record, isEditing: true });

      checkForms = [ 
        ...checkForms.slice(0, findIndex),
        modified,
        ...checkForms.slice(findIndex)
      ];
       
      BCMSStateManagerCRUD.init(state)
      .update([ "ui", "checkForms" ], checkForms)
      .submit(setState);
    },

    onClear: () => {
        const search = state.data.search;
        let checkForms = state.data.search.checkForms;

        BCMSStateManagerCRUD.init(state)
        .update([ "data", "search" ], search.resetFieldsOnly())
        .update([ "data", "search", "editCheckForms"], checkForms)
        .update([ "data", "checkForms" ], [])
        .update([ "ui", "checkForms" ], [])
        .update([ "ui", "search", "didSearch", "isActive" ], false)
        .update([ "ui", "search", "didSaveChanges", "isActive" ], false)
        .submit(setState);
    },

    onSearchMobileButtonClick: evt => {
      const mobileIsActive = state.ui.search.mobile.isActive;

      BCMSStateManagerCRUD.init(state)
      .update([ "ui", "search", "mobile", "isActive" ], !mobileIsActive)
      .submit(setState);
    },

    onSearch: () => {
        const filteredItems = state.data.search.filter();
        
        const checkFormUIs = filteredItems.reduce((acc, item, index) => {
          const mobile = { isActive: index === 0 ? true : false };

          acc.push(
            TASPositivePayCheckFormUI.init({ id: item.id, checkForm: item, isEditing: false, mobile })
          );
          return acc;
        }, []);

        BCMSStateManagerCRUD.init(state)
        .update([ "data", "checkForms" ], filteredItems)
        .update([ "ui", "checkForms" ], checkFormUIs)
        .update([ "ui", "search", "didSearch", "isActive" ], true)
        .update([ "ui", "search", "didSaveChanges", "isActive" ], false)
        .submit(setState);
    },

    onSaveClick: evt => {
      let editCheckForms = state.data.search.editCheckForms;
      let modifiedCheckForms = editCheckForms.reduce((acc, item) => {
        acc.push(
          TASPositivePayCheckForm.init(item.record)
        );
        return acc;
      }, []);

      let modifiedCheckFormsUI = editCheckForms.reduce((acc, item, index) => {
        const mobile = { isActive: index === 0 ? true : false };

        acc.push(
          TASPositivePayCheckFormUI.init({ id: item.id, checkForm: item, isEditing: false, mobile })
        );
        return acc;
      }, []);

      const filteredItems = state.data.search.filter();

      const filterCheckForms = item => {
        let found = false;

        for(let i = 0; i < filteredItems.length; i++) {
          const currFilteredItem = filteredItems[i];

          if(currFilteredItem.id === item.id) {
            found = true;
          }
        }

        return found;
      };

      let modifiedCheckFormsFiltered = modifiedCheckForms.filter(item => {
        return filterCheckForms(item);
      });

      let modifiedCheckFormsUIFiltered = modifiedCheckFormsUI.filter(item => {
        return filterCheckForms(item);
      });

      BCMSStateManagerCRUD.init(state)
        .update([ "data", "checkForms" ], modifiedCheckFormsFiltered)
        .update([ "data", "search", "checkForms" ], modifiedCheckForms)
        .update([ "ui", "checkForms" ], modifiedCheckFormsUIFiltered)
        .update([ "ui", "search", "didSaveChanges", "isActive" ], true)
        .submit(setState);
    },

    onCancelClick: evt => {
      let checkForms = state.data.search.checkForms;
      let modifiedCheckForms = checkForms.reduce((acc, item) => {
        acc.push(
          TASPositivePayCheckForm.init(item.record)
        );
        return acc;
      }, []);

      let modifiedCheckFormsUI = checkForms.reduce((acc, item) => {
        acc.push(
          TASPositivePayCheckFormUI.init({ id: item.id, checkForm: item, isEditing: false })
        );
        return acc;
      }, []);

      BCMSStateManagerCRUD.init(state)
      .update([ "data", "checkForms" ], modifiedCheckForms)
      .update([ "data", "search", "editCheckForms" ], modifiedCheckForms)
      .update([ "ui", "checkForms" ], modifiedCheckFormsUI)
      .submit(setState);
    },

    onMobileCloseButtonClick: id => evt => {
      let checkForms = state.ui.checkForms;
      const findSelectedIndex = checkForms.findIndex(item => item.id === id);
      const selectedCheckForm = checkForms.filter(item => item.id === id)[0];
      checkForms = checkForms.filter(item => item.id !== id);
      const modifiedCheckForm = TASPositivePayCheckFormUI.init({ 
        ...selectedCheckForm.record,
          mobile: { isActive: !selectedCheckForm.mobile.isActive } 
      });

      checkForms = [
        ...checkForms.slice(0, findSelectedIndex),
        modifiedCheckForm,
        ...checkForms.slice(findSelectedIndex)
      ];

      BCMSStateManagerCRUD.init(state)
      .update([ "ui", "checkForms" ], checkForms)
      .submit(setState);
    },

    onMobileSaveClick: id => evt => {
      let filteredCheckForms = state.data.checkForms;
      let filteredCheckFormsUI = state.ui.checkForms;
      let checkForms = state.data.search.checkForms;
      let editCheckForms = state.data.search.editCheckForms;
      const selectedEditCheckForm = editCheckForms.filter(item => item.id === id)[0];

      filteredCheckForms = filteredCheckForms.reduce((acc, item) => {
        if(item.id === id) {
          acc.push(
            TASPositivePayCheckForm.init(selectedEditCheckForm.record)
          );
        } else {
          acc.push(item);
        }
        return acc;
      }, []);

      checkForms = checkForms.reduce((acc, item) => {
        if(item.id === id) {
          acc.push(
            TASPositivePayCheckForm.init(selectedEditCheckForm.record)
          );
        } else {
          acc.push(item);
        }
        return acc;
      }, []);

      filteredCheckFormsUI = filteredCheckFormsUI.reduce((acc, item) => {
        if(item.id === id) {
          acc.push(
            TASPositivePayCheckFormUI.init({ ...item.record, checkForm: selectedEditCheckForm, isEditing: false })
          );
        } else {
          acc.push(item);
        }
        return acc;
      }, []);

      BCMSStateManagerCRUD.init(state)
      .update([ "data", "checkForms" ], filteredCheckForms)
      .update([ "data", "search", "checkForms" ], checkForms)
      .update([ "data", "search", "editCheckForms" ], checkForms)
      .update([ "ui", "checkForms" ], filteredCheckFormsUI)
      .update([ "ui", "search", "didSaveChanges", "isActive" ], true)
      .submit(setState);
    },

    onMobileCancelClick: id => evt => {
      let filteredCheckForms = state.data.checkForms;
      let filteredCheckFormsUI = state.ui.checkForms;
      let checkForms = state.data.search.checkForms;
      let editCheckForms = state.data.search.editCheckForms;
      const selectedCheckForm = checkForms.filter(item => item.id === id)[0];

      filteredCheckForms = filteredCheckForms.reduce((acc, item) => {
        if(item.id === id) {
          acc.push(
            TASPositivePayCheckForm.init(selectedCheckForm.record)
          );
        } else {
          acc.push(item);
        }
        return acc;
      }, []);

      checkForms = checkForms.reduce((acc, item) => {
        if(item.id === id) {
          acc.push(
            TASPositivePayCheckForm.init(selectedCheckForm.record)
          );
        } else {
          acc.push(item);
        }
        return acc;
      }, []);

      filteredCheckFormsUI = filteredCheckFormsUI.reduce((acc, item) => {
        if(item.id === id) {
          acc.push(
            TASPositivePayCheckFormUI.init({ ...item.record, checkForm: selectedCheckForm, isEditing: false })
          );
        } else {
          acc.push(item);
        }
        return acc;
      }, []);

      BCMSStateManagerCRUD.init(state)
      .update([ "data", "checkForms" ], filteredCheckForms)
      .update([ "data", "search", "checkForms" ], checkForms)
      .update([ "data", "search", "editCheckForms" ], checkForms)
      .update([ "ui", "checkForms" ], filteredCheckFormsUI)
      .submit(setState);
    }
  });

  return (
    <PageContainer className="tas-positive-pay-view-checks-page" state={ { data, ui } } controller={ controller }>
      <PageContent />
    </PageContainer>
  );
}

/**
 * @function getDefaultData
 * @return {OBJECT}
 * @exported true
 * @description Data object for the page.
 */
export const getDefaultData = (searchVals) => {
  let vals = {
      payee: "",
      status: "all",
      checkNumberFrom: "",
      checkNumberTo: "",
      issuedDateFrom: "",
      issuedDateTo: "",
      minimumAmount: "",
      maximumAmount: "",
  };

  vals = { ...vals, ...searchVals ?? {} };

  return {
      search: TASPositivePayViewChecksSearch.init(vals),
      checkForms: []
  };
};

/**
 * @function getDefaultUI
 * @return {OBJECT}
 * @exported true
 * @description UI object for the page.
 */
export const getDefaultUI = (search) => {
  return {
    search: TASPositivePayViewChecksSearchUI.init({ 
      search: search,
      didSearch:  { isActive: false },
      didSaveChanges: { isActive: false },
      mobile: { isActive: true },
      account: { isActive: false },
      status: { isActive: false },
      issuedDateFrom: { isActive: false },
      issuedDateTo: { isActive: false },
      cancelModal: { isActive: false }
    }),
    checkForms: [],
  };
};

/**
 * @function PageContent
 * @return {React.Component}
 * @exported true
 * @description Content for the page.
 */
function PageContent(props) {
  return (
    <PageContentView>
         <WidgetContainer className="tas-positive-pay-view-checks-widget">
            <WidgetView text="View Checks">
                { <TASPositivePayViewChecksDesktopFormView/> }
                { <TASPositivePayViewChecksMobileFormView/> }
            </WidgetView>
        </WidgetContainer>
    </PageContentView>
  );
}